import { insightEngine } from '../insights'
import { pricingAdvisor } from '../pricing'
import { recommendationEngine } from '../recommendations'
import { quoteAnalyzer, supplierAdvisor } from '../analysis'
import type {
  AIConfig,
  AIInsightBundle,
  AIStructuredResponse,
  CommercialObservationsResult,
  PricingAdvice,
  PurchaseAnalysisInput,
  PurchaseAnalysisResult,
  QuoteAnalysisInput,
  QuoteAnalysisResult,
  SupplierAdvice,
  TechnicalDescriptionResult,
} from '../types'
import { AI_DISCLAIMER, DEFAULT_AI_CONFIG } from '../types'
import * as prompts from '../prompts'
import type { IAssistantProvider } from '../providers/IAssistantProvider'
import type { SupplierCandidate } from '../analysis/SupplierAdvisor'

/**
 * AIEngine — builds context, runs local analysis engines, returns structured responses.
 * Never mutates quotes; recommendations always include reasons.
 */
export class AIEngine implements IAssistantProvider {
  private config: AIConfig

  constructor(config: Partial<AIConfig> = {}) {
    this.config = { ...DEFAULT_AI_CONFIG, ...config }
  }

  getConfig(): AIConfig {
    return { ...this.config }
  }

  setConfig(config: Partial<AIConfig>): AIConfig {
    this.config = {
      ...this.config,
      ...config,
      temperature: clamp(config.temperature ?? this.config.temperature, 0, 1),
      maxTokens: Math.max(64, Math.floor(config.maxTokens ?? this.config.maxTokens)),
    }
    return this.getConfig()
  }

  async analyzeQuote(
    input: QuoteAnalysisInput,
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<QuoteAnalysisResult>> {
    void prompts.quoteAnalysisPrompt(input)
    const cfg = this.merge(config)
    const { analysis, recommendations } = recommendationEngine.fromQuote(input)
    return this.wrap('analyze_quote', cfg, {
      ...analysis,
      recommendations: [...analysis.recommendations, ...recommendations].slice(0, 20),
    })
  }

  async analyzePurchase(
    input: PurchaseAnalysisInput,
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<PurchaseAnalysisResult>> {
    void prompts.purchaseAnalysisPrompt(input)
    const cfg = this.merge(config)
    const bySupplier = new Map<string, number>()
    for (const item of input.items) {
      bySupplier.set(
        item.supplier,
        (bySupplier.get(item.supplier) ?? 0) + item.unitPrice * item.quantity,
      )
    }
    const preferred = [...bySupplier.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([s]) => s)

    const productCost = input.items.reduce((s, i) => s + i.unitPrice * i.quantity, 0)
    const potentialEconomy = round2(productCost * 0.03)
    const maxDelivery = Math.max(0, ...input.items.map((i) => i.deliveryDays))

    const result: PurchaseAnalysisResult = {
      summary: `Cesta com ${input.items.length} itens, custo ~${productCost.toFixed(2)}, prazo máx ${maxDelivery}d.`,
      potentialEconomy,
      preferredSuppliers: preferred,
      recommendations: [
        {
          id: crypto.randomUUID(),
          kind: 'supplier',
          title: 'Consolidar pedidos',
          suggestion: preferred[0]
            ? `Avaliar concentrar volume em ${preferred[0]}`
            : 'Revisar fornecedores da cesta',
          reason: 'Menos pedidos reduzem frete e complexidade operacional',
          confidence: 0.74,
          requiresConfirmation: true,
        },
        {
          id: crypto.randomUUID(),
          kind: 'general',
          title: 'Economia potencial',
          suggestion: `Buscar ~${potentialEconomy.toFixed(2)} via comparação/otimização`,
          reason: 'Estimativa conservadora de 3% sobre o custo de produtos',
          confidence: 0.65,
          requiresConfirmation: true,
        },
      ],
    }
    return this.wrap('analyze_purchase', cfg, result)
  }

  async recommendSupplier(
    input: {
      product: string
      candidates: SupplierCandidate[]
    },
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<SupplierAdvice>> {
    void prompts.supplierPrompt(input)
    const cfg = this.merge(config)
    const advice = supplierAdvisor.advise(input.product, input.candidates)
    return this.wrap('recommend_supplier', cfg, advice)
  }

  async recommendPrice(
    input: {
      description: string
      purchasePrice: number
      currentSalePrice?: number
      targetMargin?: number
      category?: string
    },
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<PricingAdvice>> {
    void prompts.pricingPrompt(input)
    const cfg = this.merge(config)
    const advice = pricingAdvisor.advise(input)
    return this.wrap('recommend_price', cfg, advice)
  }

  async generateDescription(
    input: QuoteAnalysisInput,
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<TechnicalDescriptionResult>> {
    void prompts.descriptionPrompt(input)
    const cfg = this.merge(config)
    const analysis = quoteAnalyzer.analyze(input)
    const names = input.items.map((i) => i.description).slice(0, 5)
    const result: TechnicalDescriptionResult = {
      description: `Proposta comercial com ${input.items.length} item(ns): ${names.join('; ') || 'itens diversos'}.`,
      diagnosis:
        analysis.averageMargin < (input.targetMargin ?? 20)
          ? 'Diagnóstico: rentabilidade abaixo da meta em parte do pedido.'
          : 'Diagnóstico: estrutura de preços alinhada à política comercial.',
      solution:
        analysis.recommendations[0]?.suggestion ??
        'Solução: manter composição atual e validar disponibilidade dos itens críticos.',
      observations: `Total estimado ${analysis.totalValue.toFixed(2)}. Fornecedor recorrente: ${analysis.recommendedSupplier ?? 'não identificado'}.`,
      reason: 'Texto gerado a partir dos itens, margens e riscos do orçamento informado',
    }
    return this.wrap('generate_description', cfg, truncate(result, cfg.maxTokens))
  }

  async generateObservations(
    input: QuoteAnalysisInput,
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<CommercialObservationsResult>> {
    void prompts.observationsPrompt(input)
    const cfg = this.merge(config)
    const analysis = quoteAnalyzer.analyze(input)
    const hasStockRisk = analysis.outOfStockItems.length > 0
    const result: CommercialObservationsResult = {
      prazo: hasStockRisk
        ? 'Prazo de entrega sujeito à reposição de itens sem estoque (confirmar em até 24h).'
        : 'Prazo de entrega estimado de 3 a 7 dias úteis após confirmação do pedido.',
      garantia: 'Garantia conforme fabricante, com suporte MasterHub no período de cobertura.',
      condicoes:
        'Condições: preços válidos para pagamento via Pix; cartão conforme parcelamento vigente.',
      validade: 'Validade desta proposta: 7 dias corridos a partir da emissão.',
      fullText: '',
      reason: 'Observações padronizadas ajustadas aos riscos detectados no orçamento',
    }
    result.fullText = [
      result.prazo,
      result.garantia,
      result.condicoes,
      result.validade,
    ].join('\n')
    return this.wrap('generate_observations', cfg, result)
  }

  async summarize(
    input: { text: string } | QuoteAnalysisInput | PurchaseAnalysisInput,
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<{ summary: string; reason: string }>> {
    void prompts.summarizePrompt(input)
    const cfg = this.merge(config)
    if ('text' in input) {
      const summary = input.text.slice(0, Math.min(280, cfg.maxTokens))
      return this.wrap('summarize', cfg, {
        summary,
        reason: 'Resumo truncado do texto informado pelo usuário',
      })
    }
    if ('items' in input && input.items[0] && 'salePrice' in input.items[0]) {
      const analysis = quoteAnalyzer.analyze(input as QuoteAnalysisInput)
      return this.wrap('summarize', cfg, {
        summary: analysis.summary,
        reason: 'Resumo derivado da análise estruturada do orçamento',
      })
    }
    const purchase = input as PurchaseAnalysisInput
    return this.wrap('summarize', cfg, {
      summary: `Compra com ${purchase.items.length} linhas e frete ${(purchase.freightCost ?? 0).toFixed(2)}.`,
      reason: 'Resumo derivado da cesta de compra informada',
    })
  }

  async generateInsights(
    input: QuoteAnalysisInput,
    config?: Partial<AIConfig>,
  ): Promise<AIStructuredResponse<AIInsightBundle>> {
    void prompts.insightsPrompt(input)
    const cfg = this.merge(config)
    const insights = insightEngine.generate(input)
    return this.wrap('generate_insights', cfg, insights)
  }

  private merge(config?: Partial<AIConfig>): AIConfig {
    return { ...this.config, ...config }
  }

  private wrap<T>(
    type: AIStructuredResponse<T>['type'],
    config: AIConfig,
    data: T,
  ): AIStructuredResponse<T> {
    return {
      ok: true,
      type,
      data,
      model: config.model,
      language: config.language,
      generatedAt: new Date().toISOString(),
      disclaimer: AI_DISCLAIMER,
    }
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

function truncate(
  result: TechnicalDescriptionResult,
  maxTokens: number,
): TechnicalDescriptionResult {
  const maxChars = maxTokens * 4
  const join = `${result.description}\n${result.diagnosis}\n${result.solution}\n${result.observations}`
  if (join.length <= maxChars) return result
  return {
    ...result,
    observations: result.observations.slice(0, Math.max(40, maxChars / 4)),
  }
}

export const aiEngine = new AIEngine()
